import * as HoverCardPrimitive from "@radix-ui/react-hover-card"
import * as React from "react"

import { cn } from "~/utils/helpers"

const HoverCard = ({ openDelay = 150, ...props }: HoverCardPrimitive.HoverCardProps) => (
  <HoverCardPrimitive.Root openDelay={openDelay} {...props} />
)

const HoverCardTrigger = HoverCardPrimitive.Trigger

const HoverCardTitle = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("label-base text-left", className)} {...props} />
)

const HoverCardHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-row justify-between items-center gap-1 py-3 px-4 h-11 border-b border-base",
      className,
    )}
    {...props}
  />
)

/**
 * Growing from the trigger's edge instead of the card's centre is what makes the card read as
 * emerging from what you hovered. Radix measures that point for us.
 */
const hoverCardMotion = [
  "origin-(--radix-hover-card-content-transform-origin)",
  "data-[state=open]:overlay-enter data-[state=closed]:overlay-exit",
  // Nudge the card in from the trigger's side, on top of the scale.
  "data-[side=bottom]:[--overlay-enter-y:-4px] data-[side=top]:[--overlay-enter-y:4px]",
].join(" ")

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
  <HoverCardPrimitive.Portal>
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        hoverCardMotion,
        "z-50 w-64 rounded-[12px] border border-base bg-base px-4 py-3 paragraph-sm text-fg-base shadow-flyout outline-hidden overflow-hidden",
        className,
      )}
      {...props}
    />
  </HoverCardPrimitive.Portal>
))
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName

const HoverCardFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("px-4 py-3 border-t border-base", className)} {...props} />
)

export {
  HoverCard,
  HoverCardContent,
  HoverCardTitle,
  HoverCardTrigger,
  HoverCardHeader,
  HoverCardFooter,
}
